import type { Session } from "@supabase/supabase-js";
import { type FormEvent, useEffect, useState } from "react";
import { AdminPanel } from "./AdminPanel";
import {
  type AdminPasswordSetupChallenge,
  completeAdminCodeSignIn,
  getAdminSupabaseClient,
  readAdminSession,
  registerAdminPasskey,
  requestAdminPasswordSetupCode,
  signInAdminWithPasskey,
  signOutAdmin,
} from "./lib/adminRepository";
import "./admin-panel.css";

const RAYK_ADMIN_EMAIL = "booking@raykschoerling.com";
const KATRIN_ADMIN_EMAIL = "booking@katrinschoerling.com";

function defaultAdminEmail() {
  const requestedPerson = new URLSearchParams(window.location.search).get("person");
  if (requestedPerson === "katrin") return KATRIN_ADMIN_EMAIL;
  if (requestedPerson === "rayk") return RAYK_ADMIN_EMAIL;
  return RAYK_ADMIN_EMAIL;
}

export function AdminAccess() {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [skipPasskeySetup, setSkipPasskeySetup] = useState(false);

  useEffect(() => {
    let mounted = true;

    readAdminSession()
      .then((currentSession) => {
        if (mounted) setSession(currentSession);
      })
      .catch((reason: unknown) => {
        if (mounted) setError(toMessage(reason));
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    const { data } = getAdminSupabaseClient().auth.onAuthStateChange((_event, nextSession) => {
      if (!mounted) return;
      setSession(nextSession);
      setLoading(false);
      setError(null);
    });

    return () => {
      mounted = false;
      data.subscription.unsubscribe();
    };
  }, []);

  if (loading) {
    return <AccessCard title="Verwaltung wird geöffnet" text="Sichere Anmeldung wird geprüft." />;
  }

  if (!session) {
    return <AdminLogin inheritedError={error} />;
  }

  if (session.user.user_metadata.admin_passkey_enabled !== true && !skipPasskeySetup) {
    return <PasskeySetup onSkip={() => setSkipPasskeySetup(true)} />;
  }

  return <AdminPanel />;
}

function AdminLogin({ inheritedError }: { inheritedError: string | null }) {
  const [email, setEmail] = useState(defaultAdminEmail());
  const [challenge, setChallenge] = useState<AdminPasswordSetupChallenge | null>(null);
  const [code, setCode] = useState("");
  const [codeBusy, setCodeBusy] = useState(false);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(inheritedError);

  async function signInWithPasskey() {
    setPasskeyBusy(true);
    setError(null);
    setMessage(null);

    try {
      await signInAdminWithPasskey();
    } catch (reason) {
      setError(toMessage(reason));
    } finally {
      setPasskeyBusy(false);
    }
  }

  async function requestCode() {
    setCodeBusy(true);
    setError(null);
    setMessage(null);

    try {
      const nextChallenge = await requestAdminPasswordSetupCode(email);
      setChallenge(nextChallenge);
      setCode("");
      setMessage(`Der sechsstellige Code wurde an ${email} geschickt.`);
    } catch (reason) {
      setError(toMessage(reason));
    } finally {
      setCodeBusy(false);
    }
  }

  async function signInWithCode(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setMessage(null);

    if (!challenge) {
      setError("Bitte fordere zuerst einen Code an.");
      return;
    }

    setCodeBusy(true);
    try {
      await completeAdminCodeSignIn({
        email,
        challengeId: challenge.challengeId,
        code,
      });
    } catch (reason) {
      setError(toMessage(reason));
      setCodeBusy(false);
    }
  }

  return (
    <main className="admin-login">
      <div className="admin-login__card">
        <p className="admin-kicker">Geschützter Bereich</p>
        <h1>Booking verwalten</h1>
        <p>Am schnellsten mit Face ID, Touch ID oder deinem Geräte-PIN.</p>

        <button
          className="admin-button"
          type="button"
          onClick={() => void signInWithPasskey()}
          disabled={passkeyBusy || codeBusy}
        >
          {passkeyBusy ? "Passkey wird geprüft …" : "Mit Passkey anmelden"}
        </button>

        <div className="admin-subsection">
          <h3>Noch kein Passkey?</h3>
          <p>Dann reicht ein sechsstelliger Code per E-Mail. Ein Passwort musst du nicht kennen oder eingeben.</p>

          <label className="admin-form">
            <span>E-Mail-Adresse</span>
            <input
              type="email"
              value={email}
              onChange={(event) => {
                setEmail(event.target.value.trim().toLowerCase());
                setChallenge(null);
                setCode("");
                setMessage(null);
                setError(null);
              }}
              autoComplete="username"
              required
            />
          </label>

          {!challenge ? (
            <button
              className="admin-button admin-button--quiet"
              type="button"
              onClick={() => void requestCode()}
              disabled={codeBusy}
            >
              {codeBusy ? "Code wird gesendet …" : "Code per E-Mail senden"}
            </button>
          ) : (
            <form className="admin-form" onSubmit={signInWithCode}>
              <label>
                Sechsstelliger Code
                <input
                  type="text"
                  value={code}
                  onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  required
                />
              </label>
              <button className="admin-button" type="submit" disabled={codeBusy || code.length !== 6}>
                {codeBusy ? "Anmeldung wird geöffnet …" : "Mit Code anmelden"}
              </button>
              <button
                className="admin-button admin-button--quiet"
                type="button"
                onClick={() => {
                  setChallenge(null);
                  setCode("");
                  setMessage(null);
                  setError(null);
                }}
                disabled={codeBusy}
              >
                Anderen Code anfordern
              </button>
            </form>
          )}

          <p className="admin-helper">
            Freigegeben für {RAYK_ADMIN_EMAIL} und {KATRIN_ADMIN_EMAIL}.
          </p>
        </div>

        {message ? <div className="admin-notice admin-notice--success">{message}</div> : null}
        {error ? <div className="admin-notice admin-notice--error">{error}</div> : null}

        <a className="admin-back-link" href="/">
          Zur Buchungsseite
        </a>
      </div>
    </main>
  );
}

function PasskeySetup({ onSkip }: { onSkip: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function createPasskey() {
    setBusy(true);
    setError(null);

    try {
      await registerAdminPasskey();
      window.location.reload();
    } catch (reason) {
      setError(toMessage(reason));
      setBusy(false);
    }
  }

  return (
    <main className="admin-login">
      <div className="admin-login__card">
        <p className="admin-kicker">Fast geschafft</p>
        <h1>Passkey einrichten</h1>
        <p>
          Einmal Face ID, Touch ID oder Geräte-PIN bestätigen. Danach öffnest du die Verwaltung
          normalerweise direkt per Passkey.
        </p>

        <button className="admin-button" type="button" onClick={() => void createPasskey()} disabled={busy}>
          {busy ? "Passkey wird eingerichtet …" : "Passkey jetzt einrichten"}
        </button>

        <p className="admin-helper">
          Falls du später keinen Zugriff auf deinen Passkey hast, kannst du dich jederzeit wieder
          mit einem sechsstelligen Code per E-Mail anmelden.
        </p>

        {error ? <div className="admin-notice admin-notice--error">{error}</div> : null}

        <button
          className="admin-button admin-button--quiet"
          type="button"
          onClick={onSkip}
          disabled={busy}
        >
          Für jetzt überspringen
        </button>

        <button
          className="admin-button admin-button--quiet"
          type="button"
          onClick={() => void signOutAdmin()}
          disabled={busy}
        >
          Abmelden
        </button>
      </div>
    </main>
  );
}

function AccessCard({ title, text }: { title: string; text: string }) {
  return (
    <main className="admin-login">
      <div className="admin-login__card">
        <p className="admin-kicker">Booking Verwaltung</p>
        <h1>{title}</h1>
        <p>{text}</p>
      </div>
    </main>
  );
}

function toMessage(reason: unknown): string {
  return reason instanceof Error ? reason.message : "Es ist ein unbekannter Fehler aufgetreten.";
}
